<template>
    <section class="bg-white border-t border-black">
      <div class="max-w-5xl py-8 mx-auto md:py-16">
        <!-- Upper Content -->
        <div class="max-w-3xl px-4 mx-auto text-center lg:text-left">
          <h2 class="text-xl text-black sm:text-2xl xl:text-3xl font-ww-medium" v-html="upperHeading"></h2>
          <div class="mt-6 text-base text-black sm:text-lg xl:text-xl font-ww-light mobile-point-left" v-html="upperContent"></div>
        </div>
  
        <!-- Image Section -->
        <div class="py-10 md:py-16">
          <nuxt-img
            :src="imageSrc"
            :alt="imageAlt"
            sizes="xs:330px sm:440px md:760px lg:1024px xl:989x 2xl:989px"
            loading="lazy"
            format="webp"
            width="989"
            height="437"
            class="mx-auto"
          />
        </div>
  
        <!-- Lower Content -->
        <div class="max-w-3xl px-4 mx-auto text-center lg:text-left">
          <div class="text-xl font-bold text-black sm:text-xl xl:text-3xl" v-html="lowerHeading"></div>
          <div class="mt-6 text-black sm:text-lg xl:text-xl font-ww-light" v-html="lowerContent"></div>
        </div>
      </div>
    </section>
  </template>
  
  <script setup>
  // Props for dynamic content
  const props = defineProps({
    upperHeading: {
      type: String,
      default:
        "If you've noticed your morning coffee getting more expensive lately, you're not alone...",
    },
    upperContent: {
      type: String,
      default:
        `According to Bloomberg News, coffee prices have surged over 60% in the past 18 months. And industry experts are warning this is just the beginning.<br><br>
        Here's what's happening:<br><br>
        • Brazil's worst drought in 91 years has devastated coffee crops<br>
        • Supply chain disruptions continue to drive up costs<br>
        • Major coffee chains announcing 15-25% price increases<br>
        • Climate change threatening up to 50% of coffee-growing regions<br>
        • Growing global demand creating supply shortages`,
    },
    imageSrc: {
      type: String,
      default: '/assets/images/coffee-class.png',
    },
    imageAlt: {
      type: String,
      default: 'Coffee on money',
    },
    lowerHeading: {
      type: String,
      default:
        `The result?<br><span class="text-[#F1574B]">JP Morgan analysts predict coffee prices could double in 2025.</span>`,
    },
    lowerContent: {
      type: String,
      default:
        `But while most coffee lovers will be forced to pay these skyrocketing prices or cut back on their daily rituals, a small group of savvy Lifeboost Coffee drinkers have already protected themselves...<br><br>
        Think about it...<br><br>
        • What if your morning coffee suddenly costs $8-10 per cup?<br>
        • What if you had to start rationing your favorite coffee?<br>
        • What if you had to switch to cheaper, lower-quality beans?<br><br>
        For most people, this isn't a question of "if" but "when."<br><br>
        But today, you have a chance to join the smart coffee lovers who've already secured their protection against these rising costs—while enjoying the world's healthiest, tastiest coffee.`,
    },
  });
  
  // Builder.io configuration
  defineBuilderComponent({
    friendlyName: 'FeatureWithImage',
    inputs: [
      {
        name: 'upperHeading',
        type: 'richText',
        friendlyName: 'Upper Heading',
        defaultValue:
          "If you've noticed your morning coffee getting more expensive lately, you're not alone...",
      },
      {
        name: 'upperContent',
        type: 'richText',
        friendlyName: 'Upper Content',
        defaultValue:
          `According to Bloomberg News, coffee prices have surged over 60% in the past 18 months. And industry experts are warning this is just the beginning.<br><br>
          Here's what's happening:<br><br>
          • Brazil's worst drought in 91 years has devastated coffee crops<br>
          • Supply chain disruptions continue to drive up costs<br>
          • Major coffee chains announcing 15-25% price increases<br>
          • Climate change threatening up to 50% of coffee-growing regions<br>
          • Growing global demand creating supply shortages`,
      },
      {
        name: 'imageSrc',
        type: 'file',
        friendlyName: 'Image Source',
        allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
        defaultValue: '/assets/images/coffee-class.png',
      },
      {
        name: 'imageAlt',
        type: 'text',
        friendlyName: 'Image Alt Text',
        defaultValue: 'Coffee on money',
      },
      {
        name: 'lowerHeading',
        type: 'richText',
        friendlyName: 'Lower Heading',
        defaultValue:
          `The result?<br><span class="text-[#F1574B]">JP Morgan analysts predict coffee prices could double in 2025.</span>`,
      },
      {
        name: 'lowerContent',
        type: 'richText',
        friendlyName: 'Lower Content',
        defaultValue:
          `But while most coffee lovers will be forced to pay these skyrocketing prices or cut back on their daily rituals, a small group of savvy Lifeboost Coffee drinkers have already protected themselves...<br><br>
          Think about it...<br><br>
          • What if your morning coffee suddenly costs $8-10 per cup?<br>
          • What if you had to start rationing your favorite coffee?<br>
          • What if you had to switch to cheaper, lower-quality beans?<br><br>
          For most people, this isn't a question of "if" but "when."<br><br>
          But today, you have a chance to join the smart coffee lovers who've already secured their protection against these rising costs—while enjoying the world's healthiest, tastiest coffee.`,
      },
    ],
  });
  </script>
  